
import React from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { AIStaffMember } from "@/lib/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface TerminateDialogProps {
  aiStaff: AIStaffMember;
  open: boolean;
  isTerminating: boolean;
  onOpenChange: (open: boolean) => void;
  onTerminate: () => void;
}

const TerminateDialog: React.FC<TerminateDialogProps> = ({
  aiStaff,
  open,
  isTerminating,
  onOpenChange,
  onTerminate
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-destructive">Terminate AI Staff</DialogTitle>
          <DialogDescription>
            Are you sure you want to terminate {aiStaff.name}? This action will remove them from your active staff.
            You can always hire a new AI assistant later.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="bg-destructive/10 p-4 rounded-md border border-destructive/20">
            <p className="text-sm font-medium text-destructive">What happens when you terminate an AI staff:</p>
            <ul className="mt-2 text-sm space-y-1">
              <li>• Their data will be saved but they will be marked as inactive</li>
              <li>• Your subscription for this AI staff will be canceled</li>
              <li>• You'll need to hire a new AI assistant to replace them</li>
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isTerminating}
          >
            Cancel
          </Button>
          <Button 
            variant="destructive"
            onClick={onTerminate}
            disabled={isTerminating}
          >
            {isTerminating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Terminating...
              </>
            ) : (
              'Confirm Termination'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TerminateDialog;
